import { FC, useState } from 'react'
import { rotuloNumeroSncr } from '../../../../sncr/core/_models'
import {
  ItemDoHistorico,
  SituacaoHistorico,
  documentoDaPrescricao,
  itensDaPrescricao,
  situacaoHistorico,
} from '../../core/montagem/historico'
import { ItemPrescricao } from '../../core/montagem/itens'
import { PrescricaoList } from '../../core/_model'
import { useDetalhePrescricao } from '../../hooks/useHistoricoPrescricoes'
import { IconPaths } from '_metronic/helpers'
import styles from '../../Prescricao.module.scss'
import { Icone } from '../comum/Icone'
import { useRotulos } from '../comum/useRotulos'
import { Button, CheckBox } from 'app/components'

const SITUACAO: Record<SituacaoHistorico, { marca: IconPaths; classe: string }> = {
  assinada: { marca: IconPaths.Check, classe: styles.seloProprio },
  utilizada: { marca: IconPaths.CircleCheck, classe: styles.seloApac },
  vencida: { marca: IconPaths.CalendarX, classe: styles.seloNeutro },
}

type IProps = {
  prescricao: PrescricaoList
  /** Nas últimas prescrições os itens aparecem já; no "Ver todas", só ao escolher. */
  mostrarItens: boolean
  /** Devolve quantos itens entraram (os que já estavam na prescrição ficam de fora). */
  onAdicionar: (itens: ItemPrescricao[]) => number
}

/**
 * Prescrição anterior: tipo, situação, emissão, validade, SNCR, LME/APAC e itens. "Repetir tudo"
 * copia posologia e quantidade; "Escolher itens" vira uma lista de seleção no próprio cartão.
 */
export const CartaoHistorico: FC<IProps> = ({ prescricao, mostrarItens, onAdicionar }) => {
  const { t, tituloDocumento } = useRotulos()
  const [escolhendo, setEscolhendo] = useState(false)
  const [escolhidos, setEscolhidos] = useState<string[]>([])
  const [aviso, setAviso] = useState<string>()
  const [pedido, setPedido] = useState(false)
  const detalhe = useDetalhePrescricao(prescricao.id, mostrarItens || pedido)
  const itens: ItemDoHistorico[] = detalhe.data ? itensDaPrescricao(detalhe.data) : []
  const situacao = situacaoHistorico(prescricao)
  // Sem o detalhe, só o tipo: o complemento (medicamento, tipo de exame) vem dos itens.
  const titulo = tituloDocumento(
    detalhe.data
      ? documentoDaPrescricao(detalhe.data)
      : {
          tipo: documentoDaPrescricao({ tipo: prescricao.tipo, medicamentos: [], exames: [] }).tipo,
        }
  )

  const adicionar = (selecionados: ItemDoHistorico[]) => {
    const entraram = onAdicionar(selecionados.map((linha) => linha.item))
    setAviso(
      entraram > 0
        ? t('prescricao.historico.adicionados', { quantidade: entraram })
        : t('prescricao.historico.ja_estavam')
    )
    setEscolhendo(false)
  }

  const comItens = (acao: (linhas: ItemDoHistorico[]) => void) => {
    if (detalhe.data) return acao(itensDaPrescricao(detalhe.data))
    setPedido(true)
    detalhe.refetch().then((resposta) => resposta.data && acao(itensDaPrescricao(resposta.data)))
  }

  const marcar = (chave: string) =>
    setEscolhidos((atuais) =>
      atuais.includes(chave) ? atuais.filter((c) => c !== chave) : [...atuais, chave]
    )

  const flags = [
    prescricao.lme && t('prescricao.historico.com_lme'),
    prescricao.apac && t('prescricao.historico.com_apac'),
  ].filter(Boolean)
  const descricaoAcao = `${titulo} ${t('prescricao.historico.de', {
    data: prescricao.dataFormatada,
  })}`

  return (
    <article
      className={`${styles.item} p-4 d-flex flex-column gap-3 h-100`}
      aria-label={descricaoAcao}
    >
      <div className='d-flex justify-content-between align-items-start gap-2'>
        <div className='d-flex flex-column'>
          <span className='fw-bold'>{titulo}</span>
          <span className={`fs-7 ${styles.textoSecundario}`}>
            {t('prescricao.historico.emitida', { data: prescricao.dataFormatada })}
          </span>
        </div>
        <span className={`${styles.selo} ${SITUACAO[situacao].classe}`}>
          <Icone icone={SITUACAO[situacao].marca} />
          {t(`prescricao.historico.situacao.${situacao}`)}
        </span>
      </div>

      <dl className={`m-0 fs-7 ${styles.dados}`}>
        <dt className={styles.textoSecundario}>{t('prescricao.historico.validade')}</dt>
        <dd className='m-0'>{prescricao.dataValidadeFormatada || '—'}</dd>
        <dt className={styles.textoSecundario}>SNCR</dt>
        <dd className='m-0'>
          {rotuloNumeroSncr(prescricao.numeroSncr, prescricao.tipoReceitaSncr) || '—'}
        </dd>
        {prescricao.dataUtilizacaoFormatada && (
          <>
            <dt className={styles.textoSecundario}>{t('prescricao.historico.utilizada_em')}</dt>
            <dd className='m-0'>{prescricao.dataUtilizacaoFormatada}</dd>
          </>
        )}
      </dl>
      {flags.length > 0 && (
        <span className={`fs-7 fw-bold ${styles.pendenciaAviso}`}>{flags.join(' · ')}</span>
      )}

      {escolhendo ? (
        <fieldset className='m-0 p-0 border-0 d-flex flex-column gap-2'>
          <legend className='fs-7 fw-bold mb-1'>{t('prescricao.historico.escolha')}</legend>
          {itens.map((linha) => (
            <CheckBox
              key={linha.chave}
              id={`historico-${prescricao.id}-${linha.chave}`}
              label={linha.rotulo}
              checked={escolhidos.includes(linha.chave)}
              onChange={() => marcar(linha.chave)}
            />
          ))}
          <div className='d-flex gap-2 mt-1'>
            <Button size='btn-sm' className='btn-secondary' onClick={() => setEscolhendo(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              size='btn-sm'
              className='btn-primary'
              disabled={escolhidos.length === 0}
              onClick={() => adicionar(itens.filter((linha) => escolhidos.includes(linha.chave)))}
            >
              {t('prescricao.historico.adicionar_n', { quantidade: escolhidos.length })}
            </Button>
          </div>
        </fieldset>
      ) : (
        <>
          {mostrarItens && itens.length > 0 && (
            <ul className={`m-0 ps-4 fs-7 ${styles.textoSecundario}`}>
              {itens.map((linha) => (
                <li key={linha.chave}>{linha.rotulo}</li>
              ))}
            </ul>
          )}
          <div className='d-flex flex-wrap gap-2 mt-auto'>
            <Button
              size='btn-sm'
              className='btn-primary fw-bold'
              onClick={() => comItens(adicionar)}
              ariaLabel={t('prescricao.historico.repetir_aria', { descricao: descricaoAcao })}
            >
              {t('prescricao.historico.repetir')}
            </Button>
            <Button
              size='btn-sm'
              className='btn-secondary'
              onClick={() =>
                comItens((linhas) => {
                  setEscolhidos(linhas.map((linha) => linha.chave))
                  setEscolhendo(true)
                })
              }
              ariaLabel={t('prescricao.historico.escolher_aria', { descricao: descricaoAcao })}
            >
              {t('prescricao.historico.escolher')}
            </Button>
          </div>
        </>
      )}
      <span role='status' className={`fs-8 ${styles.estadoOk}`}>
        {aviso}
      </span>
    </article>
  )
}
